import {
  EXECUTION_DETAIL_KEY,
  type ExecutionDetailKey,
  isInternalUser,
  PAYMENT_METHOD,
} from '@steerfi/shared';
import {
  ArrowLeftRight,
  Building2,
  Calendar,
  Copy,
  Download,
  FileText,
  Info,
  Loader2,
  Receipt,
  TrendingDown,
  TrendingUp,
  User,
  Wallet,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import type { PaymentWithRelations } from '@/api/payments';
import { getPaymentReceiptUrl } from '@/api/payments';
import { DetailInfoCell, DetailSectionHeader } from '@/components/detail-page-primitives';
import { EventTimeline } from '@/components/event-timeline';
import { composePaymentHistory, lineageLabel } from '@/components/payment-history.utils';
import { COMPLETED_STATUS_STYLES } from '@/components/status-chip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  focusDialogOnOpen,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';
import { formatCalendarDate, formatDate } from '@/lib/utils';

interface Props {
  payment: PaymentWithRelations | null;
  open: boolean;
  onClose: () => void;
  /** Label for external reference field (from party setting external_ref_display_name). Default "External Reference". */
  externalRefLabel?: string;
}

/**
 * Format currency amount
 */
const formatAmount = (amount: string, currencyCode: string): string => {
  const numAmount = parseFloat(amount);
  if (Number.isNaN(numAmount)) return amount;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numAmount);
};

/**
 * Get badge variant for payment status
 */
const getStatusVariant = (
  statusCode: string
): 'default' | 'secondary' | 'outline' | 'completed' | 'returned' => {
  const code = statusCode.toUpperCase();

  if (code.includes('COMPLETE')) {
    return COMPLETED_STATUS_STYLES.badge;
  }
  if (code === 'RELEASED' || code === 'REISSUED') {
    return 'outline';
  }
  if (code.includes('SUCCESS') || code.includes('APPROVED')) {
    return 'default';
  }
  if (code.includes('PENDING') || code.includes('PROCESSING') || code.includes('AWAITING')) {
    return 'secondary';
  }
  if (code.includes('RETURN')) {
    return 'returned';
  }
  if (
    code.includes('FAIL') ||
    code.includes('ERROR') ||
    code.includes('REJECT') ||
    code.includes('CANCEL')
  ) {
    return 'outline';
  }

  return 'outline';
};

const getPriorityToneClass = (priority: 'LOW' | 'NORMAL' | 'HIGH' | 'CRITICAL'): string => {
  const toneMap = {
    LOW: 'bg-slate-400',
    NORMAL: 'bg-muted-foreground',
    HIGH: 'bg-amber-500',
    CRITICAL: 'bg-destructive',
  };

  return toneMap[priority];
};

/**
 * Get badge variant for payment direction
 */
const getDirectionVariant = (
  direction: 'DEBIT' | 'CREDIT'
): 'default' | 'secondary' | 'outline' => {
  return direction === 'DEBIT' ? 'secondary' : 'default';
};

const getDirectionLabel = (direction: 'DEBIT' | 'CREDIT'): string => {
  return direction === 'DEBIT' ? 'Outgoing' : 'Incoming';
};

/**
 * Per-payment-method mapping for the settlement identifier displayed on the
 * Overview tab. `key` refers to the stable `ExecutionDetail.key` written by
 * the NACHA generator (scope='generated') and the CASS 0200 return processor
 * (scope='return_received'). `mask` is set for values that should never be
 * displayed or copied in full (e.g. card numbers).
 */
interface SettlementSpec {
  key: ExecutionDetailKey;
  label: string;
  mask?: boolean;
}

const SETTLEMENT_DISPLAY_BY_METHOD: Partial<Record<number, SettlementSpec>> = {
  [PAYMENT_METHOD.ACH]: { key: EXECUTION_DETAIL_KEY.TRACE_NUMBER, label: 'Trace #' },
  [PAYMENT_METHOD.CHECK]: { key: EXECUTION_DETAIL_KEY.CHECK_NUMBER, label: 'Check #' },
  [PAYMENT_METHOD.VIRTUAL_CARD]: {
    key: EXECUTION_DETAIL_KEY.CARD_NUMBER,
    label: 'Card #',
    mask: true,
  },
};

const maskCardNumber = (value: string): string => `•••• ${value.slice(-4)}`;

const coerceSettlementValue = (value: unknown): string | null => {
  if (typeof value === 'string') return value.length > 0 ? value : null;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
};

interface SettlementDisplay {
  label: string;
  /** Value safe to render + copy. For card numbers this is the masked form. */
  displayValue: string;
}

/**
 * Selects the settlement identifier to surface on the Payment Details modal.
 *
 * Selection rule (display-only — differs from the file-server selector which
 * filters out SETTLED/FAILED to find a *write* target):
 *   1. Iterate `payment.instructions` where direction === 'DEBIT' (settlement
 *      identifiers only apply to outgoing legs on supported rails), sorted by
 *      `created_at` ascending.
 *   2. For each instruction, look up its own `payment_method_id` in
 *      {@link SETTLEMENT_DISPLAY_BY_METHOD}. This is per-instruction rather
 *      than per-payment so mixed-method payments (e.g. ACH retried as CHECK)
 *      still surface the matching identifier.
 *   3. Return the first matching `execution_details.details[]` entry whose
 *      `key` matches the spec's expected key.
 *   4. If none is found, return null — the row is hidden entirely (no dash,
 *      no "Pending" placeholder).
 *
 * Unsupported payment methods (ACH_PLUS, wires, RTP, FEDNOW, CASH, card
 * variants other than VIRTUAL_CARD, digital wallets, etc.) short-circuit to
 * null silently.
 */
const getSettlementDisplay = (payment: PaymentWithRelations): SettlementDisplay | null => {
  const debits = (payment.instructions ?? [])
    .filter((i) => i.direction === 'DEBIT')
    // `created_at` is ISO-8601 so lexicographic sort is correct (and cheaper than Date allocation).
    .sort((a, b) => a.created_at.localeCompare(b.created_at));

  for (const instruction of debits) {
    const methodId = instruction.payment_method_id;
    const spec = methodId != null ? SETTLEMENT_DISPLAY_BY_METHOD[methodId] : undefined;
    if (!spec) continue;

    const details = instruction.instruction_details_json?.execution_details?.details;
    if (!details?.length) continue;

    const match = details.find((d) => d.key === spec.key);
    const rawValue = match ? coerceSettlementValue(match.value) : null;
    if (!rawValue) continue;

    return {
      label: spec.label,
      displayValue: spec.mask ? maskCardNumber(rawValue) : rawValue,
    };
  }

  return null;
};

const COPY_FEEDBACK_MS = 1500;

// ---------------------------------------------------------------------------
// Tab sub-components
// ---------------------------------------------------------------------------

interface OverviewTabProps {
  payment: PaymentWithRelations;
  settlement: SettlementDisplay | null;
  settlementCopied: boolean;
  onCopySettlement: (value: string) => void;
  externalRefLabel?: string | undefined;
}

const OverviewTabContent = ({
  payment,
  settlement,
  settlementCopied,
  onCopySettlement,
  externalRefLabel,
}: OverviewTabProps) => (
  <TabsContent value="overview" className="space-y-4">
    <Card className="py-0">
      <CardContent className="p-0">
        <DetailSectionHeader icon={FileText} title="Payment Information" />
        <div className="grid gap-3 p-4 sm:grid-cols-2">
          <DetailInfoCell
            label="ID"
            value={<span className="font-mono">{payment.external_id ?? payment.id}</span>}
          />
          {payment.external_reference && (
            <DetailInfoCell
              label={externalRefLabel ?? 'External Reference'}
              value={<span className="font-mono">{payment.external_reference}</span>}
            />
          )}
          {payment.transaction_type && (
            <DetailInfoCell label="Transaction Type" value={payment.transaction_type} />
          )}
          {settlement && (
            <DetailInfoCell
              label="Settlement Reference"
              value={
                <button
                  type="button"
                  onClick={() => onCopySettlement(settlement.displayValue)}
                  title={`Copy ${settlement.label}`}
                  aria-label={`Copy ${settlement.label} ${settlement.displayValue}`}
                  className="group flex items-center gap-1.5 font-mono text-xs text-foreground transition-colors hover:text-primary"
                >
                  <span>
                    <span className="text-muted-foreground mr-1">{settlement.label}</span>
                    {settlement.displayValue}
                  </span>
                  <Copy
                    className={`h-3 w-3 transition-all ${
                      settlementCopied
                        ? 'text-green-600 opacity-100'
                        : 'opacity-40 group-hover:opacity-80'
                    }`}
                  />
                </button>
              }
            />
          )}
          <DetailInfoCell
            label="Execution Date Requested"
            value={
              payment.execution_date_requested
                ? formatCalendarDate(payment.execution_date_requested)
                : 'Not specified'
            }
          />
          <DetailInfoCell label="Created At" value={formatDate(payment.created_at)} />
          <DetailInfoCell label="Updated At" value={formatDate(payment.updated_at)} />

          {payment.notes && (
            <div className="rounded-lg border bg-background/60 p-3 sm:col-span-2">
              <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Notes
              </p>
              <p className="text-sm text-foreground">{payment.notes}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  </TabsContent>
);

interface PartiesTabProps {
  payment: PaymentWithRelations;
  externalRefLabel?: string | undefined;
}

const PartiesTabContent = ({ payment, externalRefLabel }: PartiesTabProps) => (
  <TabsContent value="parties" className="space-y-4">
    <Card className="py-0">
      <CardContent className="p-0">
        <DetailSectionHeader icon={User} title="Involved Parties" />
        <div className="p-4">
          <Table caption="Involved parties">
            <TableHeader>
              <TableRow>
                <TableHead>Role</TableHead>
                <TableHead>Party Name</TableHead>
                <TableHead>{externalRefLabel ?? 'External ID'}</TableHead>
                <TableHead>Party ID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payment.owner_party && (
                <TableRow>
                  <TableCell className="font-medium">
                    <Badge variant="outline">Owner</Badge>
                  </TableCell>
                  <TableCell>{payment.owner_party.party_name}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {payment.owner_party.external_id || '—'}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {payment.owner_party.id.slice(0, 8)}...
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell className="font-medium">
                  <Badge variant="default">Payer</Badge>
                </TableCell>
                <TableCell>{payment.payer_party.party_name}</TableCell>
                <TableCell className="font-mono text-xs">
                  {payment.payer_party.external_id || '—'}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {payment.payer_party.id.slice(0, 8)}...
                </TableCell>
              </TableRow>
              {payment.payee_party && (
                <TableRow>
                  <TableCell className="font-medium">
                    <Badge variant="secondary">Payee</Badge>
                  </TableCell>
                  <TableCell>{payment.payee_party.party_name}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {payment.payee_party.external_id || '—'}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {payment.payee_party.id.slice(0, 8)}...
                  </TableCell>
                </TableRow>
              )}
              {(() => {
                const payeeParticipant = payment.instructions?.[0]?.participants?.find(
                  (p) => p.participant_role === 'PAYEE'
                );
                const payeePartySnapshot = payeeParticipant?.party_snapshot_json;

                return (
                  payeePartySnapshot && (
                    <TableRow>
                      <TableCell className="font-medium">
                        <Badge variant="secondary">Payee</Badge>
                      </TableCell>
                      <TableCell>
                        {
                          (payeePartySnapshot.display_name ||
                            payeePartySnapshot.legal_name ||
                            '—') as string
                        }
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {
                          (payeePartySnapshot.supplier_id ||
                            payeePartySnapshot.vendor_code ||
                            payeePartySnapshot.external_reference ||
                            '—') as string
                        }
                      </TableCell>
                      <TableCell className="font-mono text-xs">{'Pass Thru'}</TableCell>
                    </TableRow>
                  )
                );
              })()}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  </TabsContent>
);

interface CountsTabProps {
  payment: PaymentWithRelations;
}

const CountsTabContent = ({ payment }: CountsTabProps) => (
  <TabsContent value="counts" className="space-y-4">
    <Card className="py-0">
      <CardContent className="p-0">
        <DetailSectionHeader icon={Building2} title="Related Entities" />
        <div className="p-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border bg-background/60 p-6 text-center">
              <p className="text-3xl font-bold text-primary">{payment.instruction_count || 0}</p>
              <p className="text-sm text-muted-foreground mt-2">Payment Instructions</p>
            </div>
            <div className="rounded-lg border bg-background/60 p-6 text-center">
              <p className="text-3xl font-bold text-primary">{payment.item_count || 0}</p>
              <p className="text-sm text-muted-foreground mt-2">Payment Items</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground text-center mt-4">
            These counts represent the number of instructions and items associated with this payment
          </p>
        </div>
      </CardContent>
    </Card>
  </TabsContent>
);

interface MetadataTabProps {
  payment: PaymentWithRelations;
}

const MetadataTabContent = ({ payment }: MetadataTabProps) => (
  <TabsContent value="metadata" className="space-y-4">
    <Card className="py-0">
      <CardContent className="p-0">
        <DetailSectionHeader icon={Info} title="Additional Metadata" />
        <div className="space-y-4 p-4">
          {payment.metadata_json && Object.keys(payment.metadata_json).length > 0 ? (
            <div>
              <p className="text-sm text-muted-foreground mb-2">Metadata JSON</p>
              <pre className="text-xs bg-muted p-3 rounded-md overflow-auto max-h-60">
                {JSON.stringify(payment.metadata_json, null, 2)}
              </pre>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-6">No metadata available</p>
          )}

          {payment.idempotency_key && (
            <div className="pt-4 border-t">
              <p className="text-sm text-muted-foreground mb-1">Idempotency Key</p>
              <p className="font-mono text-xs bg-muted p-2 rounded-md break-all">
                {payment.idempotency_key}
              </p>
            </div>
          )}

          {payment.created_by_name && (
            <div className="pt-4 border-t">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Created By</p>
                  <p className="text-sm">{payment.created_by_name}</p>
                </div>
                {payment.updated_by_name && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Updated By</p>
                    <p className="text-sm">{payment.updated_by_name}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  </TabsContent>
);

interface EventLogTabProps {
  payment: PaymentWithRelations;
}

const EventLogTabContent = ({ payment }: EventLogTabProps) => (
  <TabsContent value="event-log" className="space-y-4">
    <Card className="py-0">
      <CardContent className="p-0">
        <DetailSectionHeader icon={Calendar} title="Payment History" />
        <div className="p-4">
          <EventTimeline
            key={payment.id}
            fetchEventLog={() => composePaymentHistory(payment)}
            title="Payment History"
            creationLabel="Payment Created"
            embedded
          />
        </div>
      </CardContent>
    </Card>
  </TabsContent>
);

/**
 * Presentational body for payment details (overview cards + tabbed content).
 * Shared by the {@link PaymentDetails} dialog and the standalone payment detail
 * subpage. Callers own the surrounding chrome (dialog frame / page header) and
 * horizontal padding.
 */
export const PaymentDetailsBody = ({
  payment,
  externalRefLabel,
  showMetadataTab,
}: {
  payment: PaymentWithRelations;
  externalRefLabel?: string | undefined;
  showMetadataTab: boolean;
}) => {
  const [settlementCopied, setSettlementCopied] = useState(false);
  const copyTimeoutRef = useRef<number | null>(null);

  const settlement = useMemo(() => getSettlementDisplay(payment), [payment]);

  // Clear any in-flight copy-feedback timeout on unmount to avoid setState on an
  // unmounted component if the view unmounts within the feedback window.
  useEffect(
    () => () => {
      if (copyTimeoutRef.current != null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
    },
    []
  );

  const handleCopySettlement = useCallback(async (value: string) => {
    // navigator.clipboard.writeText can reject in real-world cases:
    // insecure context (HTTP), document not focused, permission denied, browser
    // policy. Operator reconciliation tooling must not lie about copy success —
    // only show the confirmation state if the write actually succeeded.
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      toast.error('Could not copy to clipboard. Select the value to copy it manually.');
      return;
    }

    setSettlementCopied(true);
    // Reset any prior timeout so rapid clicks don't prematurely clear the feedback.
    if (copyTimeoutRef.current != null) {
      window.clearTimeout(copyTimeoutRef.current);
    }
    copyTimeoutRef.current = window.setTimeout(() => {
      setSettlementCopied(false);
      copyTimeoutRef.current = null;
    }, COPY_FEEDBACK_MS);
  }, []);

  return (
    <div className="flex flex-col gap-4">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {/* Amount Card */}
        <Card className="py-0">
          <CardContent className="flex min-h-[132px] flex-col justify-between p-4">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <Wallet className="h-3.5 w-3.5" />
              Total Amount
            </div>
            <div>
              <div className="text-2xl font-semibold tracking-tight tabular-nums">
                {formatAmount(payment.amount_total, payment.currency.code)}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{payment.currency.name}</p>
            </div>
          </CardContent>
        </Card>

        {/* Status Card */}
        <Card className="py-0">
          <CardContent className="flex min-h-[132px] flex-col justify-between p-4">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <Info className="h-3.5 w-3.5" />
              Status & Priority
            </div>
            <div>
              <Badge
                variant={getStatusVariant(payment.status.code)}
                className="rounded-full px-2.5"
              >
                {payment.status.name}
              </Badge>
              {(payment.parent_payment_id || payment.reissued_as_payment_id) && (
                <div className="mt-2 space-y-1 text-xs">
                  {payment.parent_payment_id ? (
                    <p>
                      Reissued from:{' '}
                      <Link
                        to={`/payments/${payment.parent_payment_id}`}
                        className="font-medium text-foreground underline underline-offset-4 hover:text-primary"
                      >
                        {lineageLabel(payment.parent_external_id, payment.parent_payment_id)}
                      </Link>
                    </p>
                  ) : null}
                  {payment.reissued_as_payment_id ? (
                    <p>
                      Reissued as:{' '}
                      <Link
                        to={`/payments/${payment.reissued_as_payment_id}`}
                        className="font-medium text-foreground underline underline-offset-4 hover:text-primary"
                      >
                        {lineageLabel(
                          payment.reissued_as_external_id,
                          payment.reissued_as_payment_id
                        )}
                      </Link>
                    </p>
                  ) : null}
                </div>
              )}
              <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                <span
                  className={`h-1.5 w-1.5 rounded-full ${getPriorityToneClass(payment.priority)}`}
                />
                <span>{payment.priority.toLowerCase()} priority</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Direction Card */}
        <Card className="py-0">
          <CardContent className="flex min-h-[132px] flex-col justify-between p-4">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <ArrowLeftRight className="h-3.5 w-3.5" />
              Direction
            </div>
            <div>
              <Badge
                variant={getDirectionVariant(payment.direction)}
                className="rounded-full px-2.5"
              >
                {payment.direction === 'DEBIT' ? (
                  <TrendingDown className="h-3 w-3 mr-1" />
                ) : (
                  <TrendingUp className="h-3 w-3 mr-1" />
                )}
                {getDirectionLabel(payment.direction)}
              </Badge>
              <p className="mt-3 text-xs text-muted-foreground">
                {payment.direction === 'DEBIT' ? 'Payment outgoing' : 'Payment incoming'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="flex flex-col gap-3">
        <TabsList className={`grid w-full ${showMetadataTab ? 'grid-cols-5' : 'grid-cols-4'}`}>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="parties">Parties</TabsTrigger>
          {showMetadataTab && <TabsTrigger value="metadata">Metadata</TabsTrigger>}
          <TabsTrigger value="counts">Counts</TabsTrigger>
          <TabsTrigger value="event-log">Payment History</TabsTrigger>
        </TabsList>

        <div className="pb-2">
          <OverviewTabContent
            payment={payment}
            settlement={settlement}
            settlementCopied={settlementCopied}
            onCopySettlement={handleCopySettlement}
            externalRefLabel={externalRefLabel}
          />
          <PartiesTabContent payment={payment} externalRefLabel={externalRefLabel} />
          {showMetadataTab && <MetadataTabContent payment={payment} />}
          <CountsTabContent payment={payment} />
          <EventLogTabContent payment={payment} />
        </div>
      </Tabs>
    </div>
  );
};

export const PaymentDetails = ({ payment, open, onClose, externalRefLabel }: Props) => {
  const [receiptLoading, setReceiptLoading] = useState(false);
  const { user } = useKeycloakAuth();
  const showMetadataTab = isInternalUser(user?.roles);

  const isReceiptEligible = payment?.status?.code === 'COMPLETED';

  const handleDownloadReceipt = useCallback(async () => {
    if (!payment) return;
    setReceiptLoading(true);
    try {
      const result = await getPaymentReceiptUrl(payment.id);
      if (result?.signed_url) {
        window.open(result.signed_url, '_blank');
      }
    } finally {
      setReceiptLoading(false);
    }
  }, [payment]);

  if (!payment && open) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md" onOpenAutoFocus={focusDialogOnOpen}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-primary" />
              Payment Details
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-6">
            <div className="text-destructive text-lg font-semibold mb-2">
              ⚠️ Issue Loading Details
            </div>
            <p className="text-muted-foreground">
              We couldn't load the payment details. Please try again or contact support.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!payment) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="flex max-h-[92vh] min-h-[68vh] w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl"
        onOpenAutoFocus={focusDialogOnOpen}
      >
        <div className="shrink-0 border-b px-6 pt-6 pb-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-primary" />
              Payment Details - {payment.external_id ?? `${payment.id.slice(0, 8)}...`}
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          <PaymentDetailsBody
            payment={payment}
            externalRefLabel={externalRefLabel}
            showMetadataTab={showMetadataTab}
          />
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t px-6 py-4">
          {isReceiptEligible && (
            <Button variant="secondary" onClick={handleDownloadReceipt} disabled={receiptLoading}>
              {receiptLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download Receipt
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
